import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/styles/webawesome.css';
import '../libraries/events.js';

import {
    faBuilding,
    faCircleInfo,
    faEnvelope,
    faPlus,
    faSeedling,
    type IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { css, html, LitElement, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { loadErrorCallout } from '../components/load_error_callout.js';
import { infoCalloutStyles } from '../libraries/info_callout_styles.js';
import { getPendingInvitationCount } from '../libraries/invitations_store.js';
import { redirectTo } from '../libraries/navigation.js';
import { onWindowEvent, WindowEventsController } from '../libraries/window_events.js';

@customElement('fm-home')
export class FmHome extends LitElement {

    static styles = [
        infoCalloutStyles,
        css`
        :host {
            display: flex;
            flex-direction: column;
            gap: var(--wa-space-l);
        }

        h1 {
            margin: 0;
            font-size: var(--wa-font-size-l);
            font-weight: var(--wa-font-weight-bold);
            color: var(--fm-color-text-normal);
        }

        .description {
            margin: 0;
            font-size: var(--wa-font-size-s);
            color: var(--fm-color-text-quiet);
            line-height: 1.5;
        }

        .invitations-callout::part(message) {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: var(--wa-space-m);
            overflow: visible;
        }

        .cards {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: var(--wa-space-xl);
        }

        .card {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: var(--wa-space-m);
            padding: var(--wa-space-3xl) var(--wa-space-xl);
            background: var(--fm-color-surface);
            border: var(--wa-border-width-s) solid var(--fm-color-border);
            border-radius: var(--fm-border-radius-card);
            text-align: center;
        }

        .card-icon {
            width: 2rem;
            height: 2rem;
            font-size: var(--wa-font-size-2xl);
            color: var(--fm-color-brand-loud);
        }

        .card-title {
            margin: 0;
            font-size: var(--wa-font-size-m);
            font-weight: var(--wa-font-weight-bold);
            color: var(--fm-color-text-normal);
        }

        .card-description {
            max-width: 28ch;
            font-size: var(--wa-font-size-s);
            color: var(--fm-color-text-quiet);
            line-height: 1.5;
        }

        @media (max-width: 40rem) {
            .cards {
                grid-template-columns: 1fr;
            }
        }
    `,
    ];

    @state()
    private pendingInvitationCount: number = 0;

    @state()
    private loadFailed = false;

    private readonly windowEvents = new WindowEventsController(this, [
        onWindowEvent('fm-invitations-changed', () => {
            void this.refreshPendingInvitationCount();
        }),
    ]);

    override connectedCallback (): void {
        super.connectedCallback();
        void this.refreshPendingInvitationCount(true);
    }

    /**
     * Fetches the pending-invitation count. The load-error callout is
     * reserved for the initial page-content load and explicit retry; a
     * failed background refresh (driven by the `fm-invitations-changed`
     * window event) leaves the already-rendered page untouched rather
     * than raising a banner the user did not trigger.
     * @param surfaceFailure - Whether a failure may raise the callout.
     */
    private async refreshPendingInvitationCount (surfaceFailure = false): Promise<void> {
        try {
            this.pendingInvitationCount = await getPendingInvitationCount();
            this.loadFailed = false;
        } catch {
            if (surfaceFailure) {
                this.pendingInvitationCount = 0;
                this.loadFailed = true;
            }
        }
    }

    private readonly handleRetry = (): void => {
        this.loadFailed = false;
        void this.refreshPendingInvitationCount(true);
    };

    private readonly handleCreateOrganization = (): void => {
        redirectTo('/organizations/new');
    };

    private readonly handleCreateFarm = (): void => {
        redirectTo('/farms/new');
    };

    private readonly handleOpenInvitations = (): void => {
        redirectTo('/invitations');
    };

    private renderInvitationsCallout (): TemplateResult | typeof nothing {
        if (this.pendingInvitationCount === 0) {
            return nothing;
        }

        const message = this.pendingInvitationCount === 1 ?
            'Sie haben 1 offene Einladung.' :
            `Sie haben ${this.pendingInvitationCount} offene Einladungen.`;

        return html`
            <wa-callout variant="brand" class="invitations-callout">
                <fm-icon slot="icon" .icon=${faEnvelope}></fm-icon>
                <span>${message}</span>
                <wa-button appearance="outlined" size="s"
                           @click=${this.handleOpenInvitations}>
                    Einladungen ansehen
                </wa-button>
            </wa-callout>
        `;
    }

    private renderCard (
        icon: IconDefinition,
        title: string,
        description: string,
        buttonLabel: string,
        onClick: () => void
    ): TemplateResult {
        return html`
            <section class="card">
                <fm-icon class="card-icon" .icon=${icon}></fm-icon>
                <h2 class="card-title">${title}</h2>
                <span class="card-description">${description}</span>
                <wa-button size="s" variant="brand" @click=${onClick}>
                    <fm-icon slot="start" .icon=${faPlus}></fm-icon>
                    ${buttonLabel}
                </wa-button>
            </section>
        `;
    }

    render (): TemplateResult {
        return html`
            ${loadErrorCallout(
                this.loadFailed,
                'Die Einladungen konnten nicht geladen werden.',
                this.handleRetry
            )}

            ${this.renderInvitationsCallout()}

            <h1>Willkommen</h1>
            <p class="description">
                Um zu beginnen, legen Sie eine Organisation oder einen Betrieb an.
            </p>

            <div class="cards">
                ${this.renderCard(
                    faBuilding,
                    'Organisation anlegen',
                    'Organisationen bündeln mehrere Betriebe unter einem Dach.',
                    'Organisation anlegen',
                    this.handleCreateOrganization
                )}
                ${this.renderCard(
                    faSeedling,
                    'Betrieb anlegen',
                    'Ein einzelner Betrieb ohne übergeordnete Organisation.',
                    'Betrieb anlegen',
                    this.handleCreateFarm
                )}
            </div>

            <wa-callout variant="neutral">
                <fm-icon slot="icon" .icon=${faCircleInfo}></fm-icon>
                Eine Organisation kann auch nachträglich einem Betrieb zugeordnet werden.
            </wa-callout>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fm-home': FmHome;
    }
}
